import { useState, useRef, useCallback, useEffect } from "react";
import api from "../utils/api";
import { windColor, degToCompass, beaufort, beaufortLabel } from "../utils/wind";
import { useAuth } from "../hooks/useAuth";
import {
  SVG_W, SVG_H,
  ANT_TOP, ANT_H, ROOF_H,
  BUILDING_TOP, FLOOR_H, STREET_H,
  BUILDING_BODY_H, TOTAL_H, CONTAINER_H,
  PAD_X, DIV_W, BUILDING_W, UNIT_W,
  FLOORS, HOUSE_NUMS,
} from "../utils/building-layout";

// Native Breite des gesamten Plans: 2x Etagenlabels (38px) + Gebaeude-SVG
const PLAN_W = SVG_W + 76;

export default function BuildingPlan({ sensors, onUpdate }) {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [editMode, setEditMode] = useState(false);
  const [logo, setLogo] = useState(localStorage.getItem("ks_logo") || null);
  const planRef = useRef(null);
  const fileRef = useRef(null);

  // Responsive: gesamten Plan als Einheit skalieren, Geometrie bleibt fix
  const outerRef = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / PLAN_W));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const draggingRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const handleDragStart = useCallback((e, sensor) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const offset = { x: e.clientX - rect.left - rect.width / 2, y: e.clientY - rect.top - rect.height / 2 };
    dragOffsetRef.current = offset;
    setDragOffset(offset);
    draggingRef.current = sensor.id;
    setDragging(sensor.id);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    const sensorId = draggingRef.current;
    const offset = dragOffsetRef.current;
    if (!sensorId || !planRef.current || !isAdmin) return;
    const rect = planRef.current.getBoundingClientRect();
    const x = ((e.clientX - offset.x - rect.left) / rect.width) * 100;
    const y = ((e.clientY - offset.y - rect.top) / rect.height) * 100;
    try {
      await api.put(`/sensors/${sensorId}`, {
        position_x: Math.max(2, Math.min(98, x)),
        position_y: Math.max(2, Math.min(98, y)),
      });
      onUpdate && onUpdate();
    } catch (err) { console.error(err); }
    draggingRef.current = null;
    setDragging(null);
  }, [isAdmin, onUpdate]);

  const positioned = sensors.filter(s => s.position_x !== null && s.position_y !== null);
  const unpositioned = sensors.filter(s => s.position_x === null || s.position_y === null);

  return (
    <div style={S.wrapper}>
      <div style={S.toolbar}>
        <div style={S.titleBlock}>
          <div style={S.logoBox} onClick={() => isAdmin && fileRef.current?.click()}>
            {logo ? <img src={logo} alt="KS" style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 6 }} /> : <span style={S.logoFallback}>KS</span>}
            {isAdmin && <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = ev => { localStorage.setItem("ks_logo", ev.target.result); setLogo(ev.target.result); }; r.readAsDataURL(f); }} />}
          </div>
          <div>
            <div style={S.titleMain}>Gebäudeübersicht Windmessung</div>
            <div style={S.titleSub}>Unterer Rheinweg 48-52, 4057 Basel</div>
          </div>
        </div>
        <div style={S.toolbarRight}>
          {isAdmin && <button style={{ ...S.btnEdit, background: editMode ? "#1d4ed8" : "#334155" }} onClick={() => setEditMode(!editMode)}>{editMode ? "Bearbeitung beenden" : "Sensoren positionieren"}</button>}
          {!isAdmin && <span style={S.readonlyTag}>Nur-Lesen</span>}
        </div>
      </div>
      {isAdmin && editMode && unpositioned.length > 0 && (
        <div style={S.unpos}>
          <span style={S.unposHint}>Noch nicht positioniert - auf Gebaeude ziehen:</span>
          {unpositioned.map(s => <div key={s.id} draggable onDragStart={e => handleDragStart(e, s)} style={S.unposBadge}>{s.name}</div>)}
        </div>
      )}
      <div ref={outerRef} style={{ width: "100%" }}>
       <div style={{ width: PLAN_W * scale, height: CONTAINER_H * scale, margin: "0 auto", overflow: "hidden" }}>
        <div style={{ ...S.buildingRow, width: PLAN_W, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        <FloorLabels />
        <div ref={planRef} style={{ ...S.building, height: CONTAINER_H, width: SVG_W, flex: "none" }} onDrop={handleDrop} onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}>
          <BuildingSVG editMode={editMode} />
          {positioned.map(s => <SensorPin key={s.id} sensor={s} draggable={editMode && isAdmin} onDragStart={handleDragStart} isDragging={dragging === s.id} />)}
          {positioned.length === 0 && !editMode && <div style={S.emptyHint}>{isAdmin ? 'Sensoren positionieren klicken und auf Gebaeude ziehen' : 'Noch keine Sensoren positioniert'}</div>}
        </div>
        <FloorLabels />
        </div>
       </div>
      </div>
      <div style={S.footer}>
        <div style={S.legend}>
          {[{ color: "#22c55e", label: "< 5.6 m/s - Ruhig" },{ color: "#eab308", label: "5.6-11 m/s - Maessig" },{ color: "#f97316", label: "11-17 m/s - Stark" },{ color: "#ef4444", label: "> 17 m/s - Sturm" }].map(({ color, label }) => (
            <div key={label} style={S.legendItem}><span style={{ ...S.legendDot, background: color }} /><span style={S.legendLabel}>{label}</span></div>
          ))}
        </div>
        <div style={S.branding}>
          <div style={S.brandLogo}>{logo ? <img src={logo} alt="KS" style={{ width: 22, height: 22, objectFit: "contain" }} /> : <span style={{ color: "#fff", fontWeight: 900, fontSize: 10 }}>KS</span>}</div>
          <span style={S.brandText}>Entwickelt von <strong style={{ color: "#60a5fa" }}>Kull Solutions GmbH</strong><span style={{ color: "#475569" }}> - Alle Rechte vorbehalten - 2026</span></span>
        </div>
      </div>
    </div>
  );
}

function FloorLabels() {
  return (
    <div style={{ display: "flex", flexDirection: "column", width: 38, flexShrink: 0, height: CONTAINER_H }}>
      <div style={{ height: BUILDING_TOP, flexShrink: 0 }} />
      {FLOORS.map((fl, fi) => {
        const isDG = fi === 0;
        const isEG = fi === FLOORS.length - 1;
        return (
          <div key={fl} style={{
            height: FLOOR_H,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: isDG ? 600 : 700,
            fontFamily: "sans-serif",
            color: isDG ? "#c8a8ff" : isEG ? "#88bbdd" : "#c8e4f8",
            whiteSpace: "nowrap",
          }}>
            {fl}
          </div>
        );
      })}
    </div>
  );
}

function BuildingSVG({ editMode }) {
  const units = [0, 1, 2].map(i => PAD_X + i * (UNIT_W + DIV_W));
  const winCols = 5;
  const winW = (UNIT_W - 20) / winCols - 4;
  const winH = FLOOR_H - 18;

  // OG floors = indices 1..6 (1. OG through 6. OG)
  const ogIndices = new Set([1, 2, 3, 4, 5, 6]);
  // Window color per floor type
  const winFill = (fi) => {
    if (fi === 0) return "rgba(30,20,60,0.85)";       // DG: purple-dark
    if (ogIndices.has(fi)) return "rgba(14,60,100,0.75)"; // 1-6 OG: medium blue
    return "rgba(10,20,50,0.9)";                        // EG: very dark
  };
  const winStroke = (fi) => {
    if (fi === 0) return "rgba(160,100,255,0.45)";      // DG: purple rim
    if (ogIndices.has(fi)) return "rgba(56,160,255,0.55)"; // OG: bright blue rim
    return "#0d1e38";                                    // EG
  };

  return (
    <svg viewBox={`0 0 ${SVG_W} ${TOTAL_H}`} style={{ width: "100%", height: "100%", display: "block", pointerEvents: editMode ? "none" : "auto" }} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="skyG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#04091a" />
          <stop offset="100%" stopColor="#0a1628" />
        </linearGradient>
        <linearGradient id="roofG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3d5270" />
          <stop offset="100%" stopColor="#1e2f4a" />
        </linearGradient>
        <linearGradient id="wallG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0f2040" />
          <stop offset="100%" stopColor="#081528" />
        </linearGradient>
        <linearGradient id="dgG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a0f35" />
          <stop offset="100%" stopColor="#0f0825" />
        </linearGradient>
      </defs>

      {/* Sky background */}
      <rect x="0" y="0" width={SVG_W} height={TOTAL_H} fill="url(#skyG)" />

      {/* Antenna */}
      <line x1={SVG_W/2} y1={ANT_TOP} x2={SVG_W/2} y2={ANT_TOP + ANT_H} stroke="#8899bb" strokeWidth="3" />
      <circle cx={SVG_W/2} cy={ANT_TOP} r="5" fill="#aabbcc" />
      <line x1={SVG_W/2 - 18} y1={ANT_TOP + 8} x2={SVG_W/2} y2={ANT_TOP + 8} stroke="#8899bb" strokeWidth="2" />
      <line x1={SVG_W/2 + 18} y1={ANT_TOP + 8} x2={SVG_W/2} y2={ANT_TOP + 8} stroke="#8899bb" strokeWidth="2" />
      <circle cx={SVG_W/2 - 21} cy={ANT_TOP + 8} r="5" fill="none" stroke="#aabbcc" strokeWidth="2" />
      <circle cx={SVG_W/2 + 21} cy={ANT_TOP + 8} r="5" fill="none" stroke="#aabbcc" strokeWidth="2" />
      <text x={SVG_W/2 + 28} y={ANT_TOP + 28} fill="#a0c8e8" fontSize="11" fontFamily="sans-serif" fontWeight="500">vorh. Windmesser</text>

      {/* Roof */}
      <path d={`M${PAD_X},${BUILDING_TOP} L${PAD_X},${ANT_TOP + ANT_H} Q${SVG_W/2},${ANT_TOP + ANT_H - ROOF_H / 1.5} ${PAD_X + BUILDING_W},${ANT_TOP + ANT_H} L${PAD_X + BUILDING_W},${BUILDING_TOP} Z`}
        fill="url(#roofG)" stroke="#5577aa" strokeWidth="1.5" />
      {[1, 2].map(i => (
        <line key={i} x1={PAD_X + i * (UNIT_W + DIV_W) - DIV_W/2} y1={ANT_TOP + ANT_H}
          x2={PAD_X + i * (UNIT_W + DIV_W) - DIV_W/2} y2={BUILDING_TOP}
          stroke="#334155" strokeWidth="1" strokeDasharray="5,4" opacity="0.6" />
      ))}

      {/* Building body */}
      <rect x={PAD_X} y={BUILDING_TOP} width={BUILDING_W} height={BUILDING_BODY_H} fill="url(#wallG)" stroke="#1e3a5f" strokeWidth="1.5" />

      {/* DG background band (darker, purple tint) */}
      <rect x={PAD_X} y={BUILDING_TOP} width={BUILDING_W} height={FLOOR_H}
        fill="url(#dgG)" stroke="#3d1f6e" strokeWidth="1.5" opacity="0.95" />

      {/* Floors */}
      {FLOORS.map((fl, fi) => {
        const fy = BUILDING_TOP + fi * FLOOR_H;
        const isEG = fi === FLOORS.length - 1;
        const isDG = fi === 0;
        const rowH = FLOOR_H - 1;

        return (
          <g key={fl}>
            {/* Row background tint for DG */}
            {isDG && (
              <rect x={PAD_X + 1} y={fy + 1} width={BUILDING_W - 2} height={rowH - 1}
                fill="rgba(80,30,160,0.12)" />
            )}

            {/* Floor separator line */}
            <line x1={PAD_X} y1={fy} x2={PAD_X + BUILDING_W} y2={fy}
              stroke={isEG ? "#4477aa" : isDG ? "#5533aa" : "#1e3a5f"}
              strokeWidth={isEG || isDG ? 2 : 1} />

            {/* Windows */}
            {units.map((ux, ui) =>
              Array.from({ length: winCols }).map((_, wi) => {
                const wx = ux + 22 + wi * (winW + 4);
                const wy = fy + (FLOOR_H - winH) / 2;
                return (
                  <rect key={`${ui}-${wi}`} x={wx} y={wy} width={winW} height={winH} rx="2"
                    fill={winFill(fi)}
                    stroke={winStroke(fi)}
                    strokeWidth={ogIndices.has(fi) ? 1.2 : 1} />
                );
              })
            )}
          </g>
        );
      })}

      {/* Unit dividers */}
      {[1, 2].map(i => (
        <rect key={i} x={PAD_X + i * (UNIT_W + DIV_W) - DIV_W} y={BUILDING_TOP}
          width={DIV_W} height={BUILDING_BODY_H} fill="#040c1c" />
      ))}

      {/* Street */}
      <rect x={PAD_X} y={BUILDING_TOP + BUILDING_BODY_H} width={BUILDING_W} height={STREET_H}
        fill="#050a1e" stroke="#1e3a5f" strokeWidth="1" />
      {units.map((ux, ui) => (
        <text key={ui} x={ux + UNIT_W/2} y={BUILDING_TOP + BUILDING_BODY_H + STREET_H / 2 + 5}
          textAnchor="middle" fill="#c0e0ff" fontSize="13"
          fontFamily="sans-serif" fontWeight="800">Nr. {HOUSE_NUMS[ui]}</text>
      ))}

      {/* Below street */}
      <rect x="0" y={BUILDING_TOP + BUILDING_BODY_H + STREET_H} width={SVG_W}
        height={TOTAL_H - BUILDING_TOP - BUILDING_BODY_H - STREET_H} fill="#02060f" />
      <text x={SVG_W/2} y={BUILDING_TOP + BUILDING_BODY_H + STREET_H + 22}
        textAnchor="middle" fill="#334466" fontSize="11"
        fontFamily="sans-serif" letterSpacing="4" fontWeight="600">
        Unterer Rheinweg
      </text>
    </svg>
  );
}

function SensorPin({ sensor, draggable, onDragStart, isDragging }) {
  const [hover, setHover] = useState(false);
  const color = windColor(sensor.wind_speed);
  const speed = sensor.wind_speed != null ? sensor.wind_speed.toFixed(1) : "-";
  const gust = sensor.wind_gust != null ? sensor.wind_gust.toFixed(1) : "-";
  const bf = beaufort(sensor.wind_speed);
  return (
    <div draggable={draggable} onDragStart={e => onDragStart(e, sensor)} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ position: "absolute", left: `${sensor.position_x}%`, top: `${sensor.position_y}%`, transform: "translate(-50%,-50%)", cursor: draggable ? "grab" : "default", opacity: isDragging ? 0.3 : 1, zIndex: hover ? 20 : 5 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(4,9,26,0.96)", border: `2px solid ${color}`, borderRadius: 8, padding: "4px 10px", boxShadow: `0 0 12px ${color}55, 0 2px 8px rgba(0,0,0,0.9)`, whiteSpace: "nowrap" }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0, boxShadow: `0 0 6px ${color}` }} />
        <span style={{ color: "#e8f0ff", fontWeight: 800, fontSize: 12 }}>{sensor.name}</span>
        <span style={{ color, fontWeight: 700, fontSize: 13 }}>{speed}</span>
        <span style={{ color: "#7090bb", fontSize: 11 }}>m/s</span>
      </div>
      {hover && (
        <div style={{ position: "absolute", top: "115%", left: "50%", transform: "translateX(-50%)", background: "#08112a", border: `1px solid ${color}`, borderRadius: 10, padding: "12px 16px", fontSize: 13, lineHeight: 2.0, boxShadow: "0 8px 32px rgba(0,0,0,0.97)", zIndex: 100, minWidth: 200, pointerEvents: "none" }}>
          <div style={{ fontWeight: 800, color: "#e8f0ff", fontSize: 14, marginBottom: 6 }}>{sensor.name}</div>
          <div><span style={{ color: "#5577aa" }}>Wind </span><strong style={{ color }}>{speed} m/s</strong></div>
          <div><span style={{ color: "#5577aa" }}>Boee </span><strong style={{ color: "#f97316" }}>{gust} m/s</strong></div>
          <div><span style={{ color: "#5577aa" }}>Beaufort </span><strong style={{ color: "#e8f0ff" }}>{bf ?? "-"} - {beaufortLabel(bf)}</strong></div>
          <div><span style={{ color: "#5577aa" }}>Richtung </span><strong style={{ color: "#e8f0ff" }}>{degToCompass(sensor.wind_direction)} ({sensor.wind_direction ?? "-"})</strong></div>
          {sensor.temperature != null && <div><span style={{ color: "#5577aa" }}>Temp </span><strong style={{ color: "#e8f0ff" }}>{sensor.temperature.toFixed(1)} C</strong></div>}
          {sensor.sensor_mac && <div><span style={{ color: "#5577aa" }}>Sensor-ID </span><strong style={{ color: "#94a3b8", fontFamily: "monospace" }}>{sensor.sensor_mac}</strong></div>}
          {sensor.gateway_ip && <div><span style={{ color: "#5577aa" }}>Gateway-IP </span><strong style={{ color: "#94a3b8", fontFamily: "monospace" }}>{sensor.gateway_ip}</strong></div>}
        </div>
      )}
    </div>
  );
}

const S = {
  wrapper: { display: "flex", flexDirection: "column", gap: 12 },
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", background: "#0f1e35", borderRadius: 10, padding: "12px 18px", border: "1px solid #1e3a5f" },
  titleBlock: { display: "flex", alignItems: "center", gap: 14 },
  logoBox: { width: 48, height: 48, borderRadius: 9, background: "linear-gradient(135deg,#1d4ed8,#3b82f6)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer", overflow: "hidden", boxShadow: "0 2px 10px rgba(59,130,246,0.4)" },
  logoFallback: { color: "#fff", fontWeight: 900, fontSize: 17, letterSpacing: 1 },
  titleMain: { color: "#ffffff", fontWeight: 700, fontSize: 15 },
  titleSub: { color: "#94a3b8", fontSize: 12, marginTop: 2 },
  toolbarRight: { display: "flex", gap: 10 },
  btnEdit: { color: "#e8f0ff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600 },
  readonlyTag: { color: "#5577aa", fontSize: 13, background: "#050e1c", padding: "6px 12px", borderRadius: 6 },
  unpos: { display: "flex", gap: 8, alignItems: "center", background: "#050e1c", borderRadius: 8, padding: "10px 14px", flexWrap: "wrap", border: "1px dashed #1e3a5f" },
  unposHint: { color: "#c0dff5", fontSize: 13, fontWeight: 600 },
  unposBadge: { background: "#1d4ed8", color: "#fff", borderRadius: 6, padding: "5px 14px", fontSize: 13, cursor: "grab", fontWeight: 700, boxShadow: "0 2px 8px rgba(29,78,216,0.5)" },
  buildingRow: { display: "flex", alignItems: "flex-start", gap: 0 },
  building: { position: "relative", background: "#04091a", border: "2px solid #1e3a5f", borderRadius: 12, overflow: "hidden", userSelect: "none", flex: 1 },
  emptyHint: { position: "absolute", top: "60%", left: "50%", transform: "translate(-50%,-50%)", color: "#334466", fontSize: 13, textAlign: "center", pointerEvents: "none" },
  footer: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, background: "#0f1e35", borderRadius: 10, padding: "10px 18px", border: "1px solid #1e3a5f" },
  legend: { display: "flex", gap: 16, flexWrap: "wrap" },
  legendItem: { display: "flex", alignItems: "center", gap: 6 },
  legendDot: { width: 10, height: 10, borderRadius: "50%" },
  legendLabel: { color: "#b0d0f0", fontSize: 12 },
  branding: { display: "flex", alignItems: "center", gap: 8 },
  brandLogo: { width: 28, height: 28, borderRadius: 5, background: "linear-gradient(135deg,#1d4ed8,#3b82f6)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" },
  brandText: { fontSize: 12, color: "#5577aa" },
};
